import * as THREE from "three";
import type { EnvironmentPreset } from "./environmentPresets";
import { DEFAULT_ENVIRONMENT } from "./environmentPresets";
import { observationTuning } from "./observationTuning";

export interface ObservationEnvironment {
  setPreset: (preset: EnvironmentPreset) => void;
  update: (deltaSeconds: number) => void;
}

export function createObservationEnvironment(
  scene: THREE.Scene,
  worldVector: THREE.Vector3,
  specimenRadius: number,
): ObservationEnvironment {
  const tuning = observationTuning.ambientField;
  const particleCount = tuning.particleCount;
  const flowLength = Math.max(specimenRadius * 12, 14);
  const flowRadius = Math.max(specimenRadius * 4.5, 5);
  const positions = new Float32Array(particleCount * 3);
  const speeds = new Float32Array(particleCount);
  const random = mulberry32(0x46524143);
  const basisU = perpendicularTo(worldVector);
  const basisV = new THREE.Vector3().crossVectors(worldVector, basisU).normalize();
  const point = new THREE.Vector3();

  for (let index = 0; index < particleCount; index += 1) {
    const along = (random() - 0.5) * flowLength;
    const radialDistance = Math.sqrt(random()) * flowRadius;
    const radialAngle = random() * Math.PI * 2;
    point.copy(worldVector).multiplyScalar(along)
      .addScaledVector(basisU, Math.cos(radialAngle) * radialDistance)
      .addScaledVector(basisV, Math.sin(radialAngle) * radialDistance);
    point.toArray(positions, index * 3);
    speeds[index] = tuning.speedMin + random() * (tuning.speedMax - tuning.speedMin);
  }

  const geometry = new THREE.BufferGeometry();
  const positionAttribute = new THREE.BufferAttribute(positions, 3);
  positionAttribute.setUsage(THREE.DynamicDrawUsage);
  geometry.setAttribute("position", positionAttribute);
  const material = new THREE.PointsMaterial({
    color: DEFAULT_ENVIRONMENT.particles.ambientColor,
    size: Math.max(specimenRadius * 0.012, 0.014),
    sizeAttenuation: true,
    transparent: true,
    opacity: tuning.opacity,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const particles = new THREE.Points(geometry, material);
  particles.name = "world-vector-particles";
  particles.frustumCulled = false;
  scene.add(particles);
  const gust = createVectorGust(scene, worldVector, specimenRadius, flowLength, basisU, basisV);
  const targetAmbientColor = new THREE.Color(DEFAULT_ENVIRONMENT.particles.ambientColor);

  return {
    setPreset(preset): void {
      targetAmbientColor.setHex(preset.particles.ambientColor);
      gust.setPreset(preset);
    },
    update(deltaSeconds: number): void {
      const colorEasing = 1 - Math.exp(-deltaSeconds * 1.8);
      material.color.lerp(targetAmbientColor, colorEasing);
      for (let index = 0; index < particleCount; index += 1) {
        const offset = index * 3;
        point.fromArray(positions, offset).addScaledVector(worldVector, speeds[index] * deltaSeconds * specimenRadius);
        if (point.dot(worldVector) > flowLength * 0.5) point.addScaledVector(worldVector, -flowLength);
        point.toArray(positions, offset);
      }
      positionAttribute.needsUpdate = true;
      gust.update(deltaSeconds);
    },
  };
}

function createVectorGust(
  scene: THREE.Scene,
  worldVector: THREE.Vector3,
  specimenRadius: number,
  flowLength: number,
  basisU: THREE.Vector3,
  basisV: THREE.Vector3,
) {
  const tuning = observationTuning.vectorGust;
  const positions = new Float32Array(tuning.particleCount * 3);
  const radialSamples = new Float32Array(tuning.particleCount);
  const angleSamples = new Float32Array(tuning.particleCount);
  const depthSamples = new Float32Array(tuning.particleCount);
  const random = mulberry32(0x47555354);
  const point = new THREE.Vector3();

  for (let index = 0; index < tuning.particleCount; index += 1) {
    radialSamples[index] = Math.pow(random(), 1.6);
    angleSamples[index] = random() * Math.PI * 2;
    depthSamples[index] = random() - 0.5;
  }
  let currentWidth = randomWidth();

  function updateShape(): void {
    for (let index = 0; index < tuning.particleCount; index += 1) {
      const radialDistance = radialSamples[index] * currentWidth * specimenRadius;
      const radialAngle = angleSamples[index];
      const depth = depthSamples[index] * tuning.depthInRadii * specimenRadius;
      point.copy(worldVector).multiplyScalar(depth)
        .addScaledVector(basisU, Math.cos(radialAngle) * radialDistance)
        .addScaledVector(basisV, Math.sin(radialAngle) * radialDistance);
      point.toArray(positions, index * 3);
    }
  }

  function randomWidth(): number {
    return THREE.MathUtils.lerp(tuning.widthInRadii.min, tuning.widthInRadii.max, random());
  }
  updateShape();

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const material = new THREE.PointsMaterial({
    color: DEFAULT_ENVIRONMENT.particles.gustColor,
    map: createSoftParticleTexture(),
    alphaTest: 0.008,
    size: specimenRadius * tuning.particleSizeInRadii,
    sizeAttenuation: true,
    transparent: true,
    opacity: 0,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const cloud = new THREE.Points(geometry, material);
  cloud.name = "world-vector-gust";
  cloud.frustumCulled = false;
  scene.add(cloud);

  // Begin the first cycle near the artwork so the effect is discoverable;
  // subsequent cycles traverse the full field after the quiet interval.
  let elapsedSeconds = tuning.durationSeconds * 0.38;
  const targetColor = new THREE.Color(DEFAULT_ENVIRONMENT.particles.gustColor);

  return {
    setPreset(preset: EnvironmentPreset): void {
      targetColor.setHex(preset.particles.gustColor);
    },
    update(deltaSeconds: number): void {
      const colorEasing = 1 - Math.exp(-deltaSeconds * 1.8);
      material.color.lerp(targetColor, colorEasing);
      const cycleSeconds = tuning.durationSeconds + tuning.quietSeconds;
      const previousElapsed = elapsedSeconds;
      elapsedSeconds = (elapsedSeconds + deltaSeconds) % cycleSeconds;
      if (elapsedSeconds < previousElapsed) {
        currentWidth = randomWidth();
        updateShape();
        geometry.attributes.position.needsUpdate = true;
      }

      const progress = elapsedSeconds / tuning.durationSeconds;
      const active = progress < 1;
      cloud.visible = active;
      if (!active) return;

      const easedProgress = progress * progress * (3 - 2 * progress);
      const along = THREE.MathUtils.lerp(-flowLength * 0.52, flowLength * 0.52, easedProgress);
      cloud.position.copy(worldVector).multiplyScalar(along)
        .addScaledVector(basisU, specimenRadius * 0.38);
      material.opacity = Math.pow(Math.sin(progress * Math.PI), 1.35) * tuning.maximumOpacity;
      cloud.rotation.x += deltaSeconds * 0.012;
      cloud.rotation.z -= deltaSeconds * 0.009;
    },
  };
}

function createSoftParticleTexture(): THREE.DataTexture {
  const size = 64;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const normalizedX = (x + 0.5) / size * 2 - 1;
      const normalizedY = (y + 0.5) / size * 2 - 1;
      const distance = Math.sqrt(normalizedX * normalizedX + normalizedY * normalizedY);
      const strength = Math.pow(Math.max(0, 1 - distance), 2.4);
      const offset = (y * size + x) * 4;
      data[offset] = 255;
      data[offset + 1] = 255;
      data[offset + 2] = 255;
      data[offset + 3] = Math.round(strength * 255);
    }
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.needsUpdate = true;
  return texture;
}

function perpendicularTo(direction: THREE.Vector3): THREE.Vector3 {
  const reference = Math.abs(direction.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
  return new THREE.Vector3().crossVectors(direction, reference).normalize();
}

function mulberry32(seed: number): () => number {
  return () => {
    seed |= 0;
    seed = seed + 0x6d2b79f5 | 0;
    let value = Math.imul(seed ^ seed >>> 15, 1 | seed);
    value = value + Math.imul(value ^ value >>> 7, 61 | value) ^ value;
    return ((value ^ value >>> 14) >>> 0) / 4294967296;
  };
}
